import React from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { processBinData } from "../processBinData";

export default function NumberFrequencyVsBinChart({ graphData, selectedUnit, isMobile, selectedBinSize }) {
  const commonProps = {
    width: "100%",
    height: isMobile ? 500 : 700,
    data: graphData,
    margin: {
      top: isMobile ? 20 : 40,
      right: isMobile ? 20 : 60,
      left: isMobile ? 20 : 80,
      bottom: isMobile ? 80 : 120,
    },
  };
  const binOpts = {
    unitValue: selectedUnit.value,
    binSizeValue: selectedBinSize?.value,
  };
  const processBinDataLocal = (data) => processBinData(data, binOpts);

const binDataRaw = processBinDataLocal(graphData);
const totalCount = graphData.length;
const binDataWithPercent = binDataRaw.map((bin) => ({
  ...bin,
  number: Math.floor(bin.number),
  percent: totalCount > 0 ? (bin.number / totalCount) * 100 : 0,
}));

const maxPct = Math.max(...binDataWithPercent.map((b) => b.percent), 0);
const yUpper = Math.max(10, Math.ceil(maxPct * 2) / 2);
const yTicks = [];
for (let v = 0; v <= yUpper + 1e-9; v += 1) yTicks.push(Number(v.toFixed(1)));
const yDomain = [0, yTicks[yTicks.length - 1] || 10];
const yTickFormatter = (v) => (Number.isInteger(v) ? `${v}.0` : v.toFixed(1));

return (
  <ResponsiveContainer {...{ ...commonProps, height: isMobile ? 600 : 750 }}>
    <ComposedChart data={binDataWithPercent}>
      <CartesianGrid strokeDasharray="3 3" stroke="#000000" strokeWidth={1} />
      <XAxis
        dataKey="binLabel"
        label={{
          value: `Length Bin (${selectedUnit.value === "English" ? "Inches" : "mm"})`,
          position: "outsideBottom",
          offset: isMobile ? 20 : 25,
          fontSize: isMobile ? 14 : 18,
          fontWeight: "bold",
          fill: "#000000",
        }}
        angle={-90}
        textAnchor="end"
        height={isMobile ? 160 : 200}
        interval={0}
        tickLine={false}
        allowDuplicatedCategory={false}
        stroke="#000000"
        strokeWidth={2}
        tick={({ x, y, payload }) => {
          const binItem = binDataWithPercent.find((b) => b.binLabel === payload.value);
          const isEmpty = binItem && binItem.number === 0;
          return (
            <text
              x={x}
              y={y + 12}
              textAnchor="end"
              fontSize={isMobile ? 10 : 12}
              fontWeight="bold"
              fill={isEmpty ? "#ff6b6b" : "#000000"}
              transform={`rotate(-90, ${x}, ${y + 10})`}
            >
              {payload.value}
            </text>
          );
        }}
      />
      <YAxis
        label={{
          value: "Percentage of Population",
          angle: -90,
          position: "insideLeft",
          style: { textAnchor: "middle" },
          fontSize: isMobile ? 14 : 18,
          fontWeight: "bold",
          fill: "#000000",
        }}
        domain={yDomain}
        tick={{ fontSize: isMobile ? 10 : 12, fontWeight: "bold", fill: "#000000" }}
        ticks={yTicks}
        tickFormatter={yTickFormatter}
        stroke="#000000"
        strokeWidth={2}
      />
      <Tooltip
        content={({ active, payload, label }) => {
          if (!active || !payload || !payload.length) return null;
          const bin = payload[0].payload;
          return (
            <div
              className="custom-tooltip"
              style={{
                background: "#fff",
                border: "1px solid #ddd",
                borderRadius: "2px",
                padding: "8px 10px",
                fontSize: "13px",
                lineHeight: "1.5",
                boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                minWidth: "180px",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  marginBottom: "6px",
                  borderBottom: "1px solid #eee",
                  paddingBottom: "4px",
                }}
              >
                Length Bin: {bin.binLabel} {selectedUnit.value === "English" ? "Inches" : "mm"}
              </div>
              <div style={{ display: "grid", gap: "4px", fontSize: "12px", color: "#666" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>Number:</span>
                  <span style={{ fontWeight: "500" }}>{bin.number}</span>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <span>% of total:</span>
                  <span style={{ fontWeight: "500" }}>{bin.percent?.toFixed(1)}%</span>
                </div>
              </div>
            </div>
          );
        }}
      />
      <Bar
        dataKey="percent"
        fill="#00897B"
        barSize={30}
        name="Percentage of Population"
      />
    </ComposedChart>
  </ResponsiveContainer>
);
}
